import React, { useState, useMemo } from 'react';
import FormattedNoteText from '../common/FormattedNoteText';

const CounselorNotesTranscriptModal = ({ client, meetings = [], onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [copiedAll, setCopiedAll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    // Format date string
    const formatDate = (dateVal) => {
        if (!dateVal) return 'Data non specificata';
        if (typeof dateVal === 'string' && dateVal.includes('/')) return dateVal;
        try {
            return new Date(dateVal).toLocaleDateString('it-IT', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (e) {
            return dateVal;
        }
    };

    const formatShortDate = (dateVal) => {
        if (!dateVal) return 'N/D';
        if (typeof dateVal === 'string' && dateVal.includes('/')) return dateVal;
        try {
            return new Date(dateVal).toLocaleDateString('it-IT');
        } catch (e) {
            return dateVal;
        }
    };

    // Filter and sort meetings chronologically
    const filteredMeetings = useMemo(() => {
        const list = [...meetings].sort((a, b) => {
            const timeA = new Date(a.date || a.timestamp || 0).getTime();
            const timeB = new Date(b.date || b.timestamp || 0).getTime();
            return timeA - timeB; // Oldest to newest for full chronological transcript
        });

        return list.filter(m => {
            const notes = (m.counselorNotes || m.notes || '').toLowerCase();
            const feeling = (m.counselorFeeling || '').toLowerCase();
            const tags = (m.counselorTags || m.tags || '').toLowerCase();
            const type = (m.observations?.meetingType || m.meetingType || 'colloquio').toLowerCase();

            const matchesSearch = !searchTerm ||
                notes.includes(searchTerm.toLowerCase()) ||
                feeling.includes(searchTerm.toLowerCase()) ||
                tags.includes(searchTerm.toLowerCase());

            const matchesType = filterType === 'all' || type === filterType;

            return matchesSearch && matchesType;
        });
    }, [meetings, searchTerm, filterType]);

    // Generate formatted full transcript text
    const generateFullTranscriptText = () => {
        let output = `=====================================================\n`;
        output += `TRASCRIZIONE NOTE CLINICHE COUNSELOR\n`;
        output += `Cliente: ${client?.name || 'Anonimo'} (ID: ${client?.id || 'N/D'})\n`;
        output += `Data Esportazione: ${new Date().toLocaleDateString('it-IT')} ${new Date().toLocaleTimeString('it-IT')}\n`;
        output += `Numero Sedute: ${filteredMeetings.length}\n`;
        output += `=====================================================\n\n`;

        filteredMeetings.forEach((m, idx) => {
            const dateStr = formatDate(m.date);
            const type = m.observations?.meetingType || m.meetingType || 'Colloquio';
            const notes = m.counselorNotes || m.notes || '(Nessuna nota registrata)';
            const feeling = m.counselorFeeling || '';
            const tags = m.counselorTags || m.tags || '';
            const exercises = m.observations?.exercises || [];
            const legacyTech = m.observations?.techniques || '';

            output += `--- SEDUTA #${idx + 1} - ${dateStr} [${type.toUpperCase()}] ---\n`;
            if (m.counselorEmoji) {
                output += `Clima/Emozione: ${m.counselorEmoji}\n`;
            }
            if (tags) {
                output += `Temi: ${tags}\n`;
            }
            if (exercises.length > 0 || legacyTech) {
                const exList = [...exercises, ...(legacyTech ? legacyTech.split(',') : [])].filter(Boolean).join(', ');
                output += `Tecniche/Esercizi: ${exList}\n`;
            }
            output += `\nNOTE DI OSSERVAZIONE:\n${notes}\n`;

            if (feeling) {
                output += `\nVISSUTO / SENTITO DEL COUNSELOR:\n${feeling}\n`;
            }

            output += `\n-----------------------------------------------------\n\n`;
        });

        return output;
    };

    const handleCopyAll = async () => {
        try {
            const text = generateFullTranscriptText();
            await navigator.clipboard.writeText(text);
            setCopiedAll(true);
            setTimeout(() => setCopiedAll(false), 2500);
        } catch (err) {
            console.error('Error copying text:', err);
            alert('Impossibile copiare il testo negli appunti');
        }
    };

    const handleCopySingle = async (meeting, index) => {
        try {
            let text = `Seduta del ${formatDate(meeting.date)}:\n`;
            text += `Note: ${meeting.counselorNotes || meeting.notes || ''}\n`;
            if (meeting.counselorFeeling) {
                text += `Counselor Feeling: ${meeting.counselorFeeling}\n`;
            }
            await navigator.clipboard.writeText(text);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        } catch (err) {
            console.error('Error copying single meeting:', err);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3 sm:p-6 backdrop-blur-sm animate-fade-in print:p-0 print:bg-white print:fixed">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 print:max-h-none print:shadow-none print:border-none print:rounded-none">
                
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-6 border-b border-indigo-900/50 flex items-center justify-between print:bg-white print:text-black print:p-0 print:mb-6 print:border-b-2 print:border-black">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-2xl print:hidden">📋</span>
                            <h2 className="text-xl sm:text-2xl font-black tracking-tight print:text-2xl">
                                Trascrizione Storica Note Counselor
                            </h2>
                        </div>
                        <p className="text-indigo-200 text-xs sm:text-sm mt-1 print:text-gray-700">
                            Cliente: <span className="font-bold text-white print:text-black">{client?.name || 'Cliente'}</span> • Totale: {meetings.length} sedute registrate
                        </p>
                    </div>

                    <div className="flex items-center gap-2 print:hidden">
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="px-3 py-1.5 rounded-xl bg-indigo-800/80 hover:bg-indigo-700 text-xs font-semibold text-white flex items-center gap-1.5 transition-colors border border-indigo-600/50 shadow-sm"
                            title="Stampa o salva come PDF"
                        >
                            <span>🖨️</span>
                            <span className="hidden sm:inline">Stampa / PDF</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleCopyAll}
                            className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm ${
                                copiedAll
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                            }`}
                            title="Copia l'intera trascrizione negli appunti"
                        >
                            <span>{copiedAll ? '✓' : '📄'}</span>
                            <span>{copiedAll ? 'Copiato!' : 'Copia Tutto'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition-colors ml-2"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Filters & Controls */}
                <div className="p-4 bg-slate-50 border-b border-gray-200/80 flex flex-wrap gap-3 items-center justify-between print:hidden">
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="🔍 Cerca nelle note, feeling o temi..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full px-3.5 py-2 bg-white border border-gray-300 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 font-medium">Tipo:</label>
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="px-3 py-2 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            <option value="all">Tutte le sedute</option>
                            <option value="colloquio">Colloqui ordinari</option>
                            <option value="primo_colloquio">Primi colloqui</option>
                            <option value="checkpoint">Checkpoint</option>
                            <option value="chiusura_percorso">Chiusure percorso</option>
                        </select>
                    </div>
                </div>

                {/* Content / Transcript list */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 print:p-0 print:space-y-6 print:overflow-visible">
                    {filteredMeetings.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <span className="text-4xl block mb-2">📝</span>
                            <p className="text-sm font-medium">Nessuna nota trovata con i filtri selezionati.</p>
                        </div>
                    ) : (
                        filteredMeetings.map((meeting, idx) => {
                            const type = meeting.observations?.meetingType || meeting.meetingType || 'colloquio';
                            const badgeConfig = {
                                colloquio: { label: 'Colloquio', icon: '🛋️', bg: 'bg-gray-100 text-gray-700' },
                                checkpoint: { label: 'Checkpoint', icon: '📊', bg: 'bg-indigo-100 text-indigo-800' },
                                primo_colloquio: { label: 'Primo Colloquio', icon: '🌱', bg: 'bg-emerald-100 text-emerald-800' },
                                chiusura_percorso: { label: 'Chiusura Percorso', icon: '🏁', bg: 'bg-amber-100 text-amber-800' }
                            };
                            const badge = badgeConfig[type] || badgeConfig.colloquio;
                            const notes = meeting.counselorNotes || meeting.notes || '';
                            const exercises = meeting.observations?.exercises || [];
                            const legacyTech = meeting.observations?.techniques || '';

                            return (
                                <div
                                    key={meeting.id || idx}
                                    className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm hover:border-indigo-200 transition-colors print:border-gray-300 print:shadow-none print:break-inside-avoid"
                                >
                                    {/* Meeting Meta Header */}
                                    <div className="flex flex-wrap justify-between items-center gap-2 border-b border-gray-100 pb-3 mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-black text-indigo-900 bg-indigo-50 px-2 py-0.5 rounded-md">
                                                #{idx + 1}
                                            </span>
                                            <span className="text-sm font-bold text-gray-900 capitalize">
                                                {formatDate(meeting.date)}
                                            </span>
                                            <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${badge.bg}`}>
                                                {badge.icon} {badge.label}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {meeting.counselorEmoji && (
                                                <span className="text-base" title="Stato d'animo">{meeting.counselorEmoji}</span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleCopySingle(meeting, idx)}
                                                className="text-[11px] text-gray-500 hover:text-indigo-600 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors print:hidden"
                                                title="Copia questa nota"
                                            >
                                                {copiedIndex === idx ? '✓ Copiata' : '📋 Copia'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Observation Notes */}
                                    <div className="mb-3">
                                        <h4 className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                            📋 Note di Osservazione Clinica
                                        </h4>
                                        <div className="text-sm text-gray-800 leading-relaxed bg-gray-50/70 p-3.5 rounded-xl border border-gray-100">
                                            <FormattedNoteText text={notes} fallback="Nessuna osservazione registrata per questa seduta." />
                                        </div>
                                    </div>

                                    {/* Counselor Feeling / Resonances */}
                                    {meeting.counselorFeeling && (
                                        <div className="mb-3">
                                            <h4 className="text-[10px] font-black uppercase tracking-wider text-indigo-400 mb-1">
                                                🧘 Vissuto & Controtransfert del Counselor
                                            </h4>
                                            <div className="text-xs text-indigo-950 italic leading-relaxed bg-indigo-50/50 p-3 rounded-xl border border-indigo-100/60">
                                                <FormattedNoteText text={meeting.counselorFeeling} />
                                            </div>
                                        </div>
                                    )}

                                    {/* Tags & Exercises */}
                                    {((meeting.counselorTags || meeting.tags) || exercises.length > 0 || legacyTech) && (
                                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 text-xs">
                                            {(meeting.counselorTags || meeting.tags) && (
                                                <div className="flex items-center gap-1">
                                                    <span className="text-[10px] font-bold text-gray-400">Temi:</span>
                                                    {(meeting.counselorTags || meeting.tags).split(',').filter(t => t.trim()).map((tag, tIdx) => (
                                                        <span key={tIdx} className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                                            #{tag.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {(exercises.length > 0 || legacyTech) && (
                                                <div className="flex items-center gap-1 ml-auto">
                                                    <span className="text-[10px] font-bold text-gray-400">Tecniche:</span>
                                                    {[...exercises, ...(legacyTech ? legacyTech.split(',') : [])].filter(Boolean).map((tech, techIdx) => (
                                                        <span key={techIdx} className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                                                            {tech.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-gray-200 flex justify-between items-center text-xs text-gray-500 print:hidden">
                    <span>Visualizzate {filteredMeetings.length} di {meetings.length} sedute</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CounselorNotesTranscriptModal;
